/**
 * Presentational send-message modal for free-text SMS/app and template SMS.
 * Preview text is display-only. onSubmit does not include it.
 *
 * Free text:
 * { mode: "freeText", personObjectIds, smsContent, attemptAppMessage }
 *
 * Person template:
 * { mode: "template", templateId, personObjectIds }
 *
 * Booking template (BOOKING_DETAILS, REQUEST_HRS), only when bookingObjectIds is set:
 * { mode: "template", templateId, personObjectIds, bookingObjectIds }
 *
 * templateId is a template key such as "CALENDAR_UPDATED".
 * hiddenTemplates hides those keys. Booking templates stay hidden without bookingObjectIds.
 *
 * SMS history is not a tab. When smsHistory is set and SMS_HISTORY_TAB is enabled
 * in tabOrder, it is a collapsed rail on the right of the Send message tab.
 */
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Col, Divider, Form, Input, Modal, Radio, Row, Select, Space, Spin, Switch, Tabs } from "antd";
import React, { useEffect, useMemo, useState } from "react";

import constants from "@constants/constants";
import { RequiredFeildName } from "@constants/promptRules";

import Loader from "@ui/feedback/loader";
import Button from "@ui/form-controls/button";
import { DraggableTitle, DraggableWrapper } from "@ui/modals/draggable-wrapper";
import { SMSCreditCounter } from "@ui/messaging/sms-credit-counter";

import {
  CANCELBTN,
  EMAIL_CANDIDATE_MODAL_LABELS,
  SEND_BTN
} from "../constants/labels";

import useMessageTemplates, { BOOKING_TEMPLATE_KEYS } from "./use-message-templates";

import "./send-message-modal.scss";

const { TextArea } = Input;
const { TabPane } = Tabs;
const { NULLIFY } = constants;
const { MESSAGE, MESSAGE_PLACEHOLDER } = EMAIL_CANDIDATE_MODAL_LABELS;

const SMS_CHAR_LIMIT = 1224;
const MODAL_WIDTH = "60%";
const MODAL_WIDTH_WITH_HISTORY = "85%";

export const TEMPLATE_TAB = "template";
export const SEND_MESSAGE_TAB = "send-message";
export const SMS_HISTORY_TAB = "sms-history";

export const DEFAULT_TAB_ORDER = [TEMPLATE_TAB, SEND_MESSAGE_TAB, SMS_HISTORY_TAB];

const PANE_TABS = new Set([TEMPLATE_TAB, SEND_MESSAGE_TAB]);

const isTabShown = (key, flags) => flags?.[key] !== false;

const recipientIdsOf = recipients =>
  (recipients || []).map(recipient => recipient.objectId).filter(Boolean);

const notifySelectedIds = () => {};

const HistoryRail = ({ open, onToggle, children }) => (
  <aside
    className={
      open
        ? "ml-1 flex min-w-0 flex-[8] flex-col border-y-0 border-r-0 border-l border-solid border-[#d9d9d9] pl-4"
        : "ml-1 flex w-11 shrink-0 flex-col border-y-0 border-r-0 border-l border-solid border-[#d9d9d9]"
    }
  >
    {open ? (
      <>
        <div className="mb-2 flex items-center justify-between font-open-sans-semibold">
          <span>SMS history</span>
          <button
            type="button"
            aria-label="Hide SMS history"
            className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-[5px] border border-solid border-[#cccccc] bg-white p-0 text-[#333333] hover:border-brand hover:text-brand"
            onClick={onToggle}
          >
            <RightOutlined />
          </button>
        </div>
        <div className="min-w-0 max-h-[420px] overflow-auto">{children}</div>
      </>
    ) : (
      <button
        type="button"
        className="flex h-full min-h-40 w-full cursor-pointer flex-col items-center gap-2 border-0 bg-transparent px-0 py-3 text-black/65 hover:text-brand"
        aria-label="Show SMS history"
        onClick={onToggle}
      >
        <LeftOutlined />
        <span className="rotate-180 text-xs tracking-wide [writing-mode:vertical-rl]">History</span>
      </button>
    )}
  </aside>
);

const SendMessageModal = ({
  open,
  embedded = false,
  onClose = () => {},
  onSubmit = () => {},
  submitting = false,
  recipients = [],
  bookingObjectIds,
  defaultTab = TEMPLATE_TAB,
  showTabs,
  tabOrder = DEFAULT_TAB_ORDER,
  hiddenTemplates = [],
  smsHistory = null,
  onSelectedIdsChange = notifySelectedIds
}) => {
  const visibleTabKeys = useMemo(
    () => tabOrder.filter(key => PANE_TABS.has(key) && isTabShown(key, showTabs)),
    [tabOrder, showTabs]
  );
  const showHistorySidebar =
    tabOrder.includes(SMS_HISTORY_TAB) &&
    isTabShown(SMS_HISTORY_TAB, showTabs) &&
    recipients.length > 0 &&
    smsHistory != null;
  const fallbackTab = visibleTabKeys.includes(defaultTab) ? defaultTab : visibleTabKeys[0];

  const allRecipientIds = useMemo(() => recipientIdsOf(recipients), [recipients]);
  const recipientIdsKey = allRecipientIds.join("|");
  const recipientOptions = useMemo(
    () =>
      recipients
        .filter(recipient => recipient.objectId)
        .map(recipient => ({
          value: recipient.objectId,
          label: recipient.name || recipient.objectId
        })),
    [recipients]
  );

  const [activeTab, setActiveTab] = useState(fallbackTab);
  const [attemptAppMessage, setAttemptAppMessage] = useState(false);
  const [smsCharCount, setSmsCharCount] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(allRecipientIds);
  const [freeTextForm] = Form.useForm();

  const displayedTab = visibleTabKeys.includes(activeTab) ? activeTab : fallbackTab;

  const personObjectIds = useMemo(
    () => selectedIds.filter(id => allRecipientIds.includes(id)),
    [selectedIds, allRecipientIds]
  );

  const { templates, loading: templatesLoading } = useMessageTemplates({
    open,
    personObjectId: personObjectIds[0],
    bookingObjectIds,
    hiddenTemplates
  });

  useEffect(() => {
    const nextIds = recipientIdsKey ? recipientIdsKey.split("|") : [];
    setSelectedIds(nextIds);
  }, [open, recipientIdsKey]);

  useEffect(() => {
    onSelectedIdsChange(personObjectIds);
  }, [personObjectIds, onSelectedIdsChange]);

  useEffect(() => {
    if (!open) {
      setActiveTab(fallbackTab);
      setAttemptAppMessage(false);
      setSmsCharCount(0);
      setHistoryOpen(false);
      freeTextForm.resetFields();
    }
  }, [open, fallbackTab, freeTextForm]);

  const handleToggle = checked => {
    setAttemptAppMessage(checked);
    if (!checked) {
      const current = freeTextForm.getFieldValue("smsContent") || "";
      if (current.length > SMS_CHAR_LIMIT) {
        freeTextForm.setFieldsValue({ smsContent: current.slice(0, SMS_CHAR_LIMIT) });
        setSmsCharCount(SMS_CHAR_LIMIT);
      }
    }
  };

  const handleTemplateSubmit = ({ templateId }) => {
    const payload = {
      mode: "template",
      templateId,
      personObjectIds
    };
    if (BOOKING_TEMPLATE_KEYS.includes(templateId)) {
      payload.bookingObjectIds = bookingObjectIds;
    }
    onSubmit(payload);
  };

  const handleFreeTextSubmit = ({ smsContent }) => {
    onSubmit({
      mode: "freeText",
      personObjectIds,
      smsContent,
      attemptAppMessage
    });
  };

  const content = (
      <Spin indicator={<Loader />} spinning={submitting} className="loaderGifPosition">
        <div className="send-message-modal">
          <div className="send-message-form">
            <div className="mb-1 flex items-center gap-2">
              <span className="shrink-0">To</span>
              <div className="min-w-0 flex-1">
                <Select
                  mode="multiple"
                  className="w-full"
                  dropdownStyle={{ zIndex: 2000 }}
                  maxTagCount="responsive"
                  optionFilterProp="label"
                  value={personObjectIds}
                  onChange={setSelectedIds}
                  options={recipientOptions}
                />
              </div>
            </div>

            {visibleTabKeys.length > 0 && (
              <Tabs activeKey={displayedTab} onChange={setActiveTab}>
                {visibleTabKeys.map(tabKey => {
                  if (tabKey === TEMPLATE_TAB) {
                    return (
                  <TabPane tab="Template" key={TEMPLATE_TAB}>
                    <Spin indicator={<Loader />} spinning={templatesLoading}>
                    <Form style={{ marginTop: 30 }} onFinish={handleTemplateSubmit}>
                      <Form.Item name="templateId" rules={[RequiredFeildName("Template")]}>
                        <Radio.Group>
                          <div className="sms-modal-textboxes">
                            {templates.map(template => (
                              <div key={template.key} className="sms-modal-textbox">
                                <Radio value={template.key}>{template.label}</Radio>
                                <div className="sms-modal-preview">{template.preview || ""}</div>
                              </div>
                            ))}
                          </div>
                        </Radio.Group>
                      </Form.Item>

                      <Row>
                        <Col span={24}>
                          The default method is app notification, switching to SMS if not
                          available.
                        </Col>
                      </Row>

                      <Divider />
                      <Form.Item>
                        <Row justify="end" align="middle">
                          <Space>
                            <Button variant="outline" onClick={onClose}>
                              {CANCELBTN}
                            </Button>
                            <Button
                              variant="primary"
                              htmlType="submit"
                              disabled={submitting || personObjectIds.length === 0}
                            >
                              {SEND_BTN}
                            </Button>
                          </Space>
                        </Row>
                      </Form.Item>
                    </Form>
                    </Spin>
                  </TabPane>
                    );
                  }
                  if (tabKey === SEND_MESSAGE_TAB) {
                    return (
                  <TabPane tab="Send message" key={SEND_MESSAGE_TAB}>
                    <div className="flex items-stretch gap-4">
                      <div
                        className={
                          historyOpen && showHistorySidebar ? "min-w-0 flex-[7]" : "min-w-0 flex-1"
                        }
                      >
                        <Form form={freeTextForm} onFinish={handleFreeTextSubmit}>
                          <Col style={{ display: "flex", justifyContent: "flex-end" }} span={24}>
                            <span>SMS</span>
                            <Switch
                              style={{ margin: "0px 10px" }}
                              checked={attemptAppMessage}
                              onChange={handleToggle}
                            />
                            <span>App</span>
                          </Col>

                          <Form.Item
                            label={MESSAGE}
                            labelCol={{ span: 24 }}
                            name="smsContent"
                            rules={[RequiredFeildName(MESSAGE)]}
                            style={{ marginBottom: 4 }}
                          >
                            <TextArea
                              placeholder={MESSAGE_PLACEHOLDER}
                              rows={6}
                              maxLength={!attemptAppMessage ? SMS_CHAR_LIMIT : undefined}
                              onChange={event => setSmsCharCount(event.target.value?.length || 0)}
                            />
                          </Form.Item>
                          {!attemptAppMessage && (
                            <SMSCreditCounter charCount={smsCharCount} className="!mt-0 !p-0" />
                          )}

                          <Col>
                            <div>SMS - Messages will be sent via SMS only.</div>
                            <div>
                              App - The default method is app notification, switching to SMS if not
                              available.
                            </div>
                          </Col>

                          <Divider style={{ margin: "8px 0" }} />
                          <Form.Item>
                            <Row justify="end" align="middle">
                              <Space>
                                <Button variant="outline" onClick={onClose}>
                                  {CANCELBTN}
                                </Button>
                                <Button
                                  variant="primary"
                                  htmlType="submit"
                                  disabled={submitting || personObjectIds.length === 0}
                                >
                                  {attemptAppMessage ? "Send to app" : "Send by sms"}
                                </Button>
                              </Space>
                            </Row>
                          </Form.Item>
                        </Form>
                      </div>
                      {showHistorySidebar ? (
                        <HistoryRail
                          open={historyOpen}
                          onToggle={() => setHistoryOpen(isOpen => !isOpen)}
                        >
                          {smsHistory}
                        </HistoryRail>
                      ) : null}
                    </div>
                  </TabPane>
                    );
                  }
                  return null;
                })}
              </Tabs>
            )}
          </div>
        </div>
      </Spin>
  );

  if (embedded) {
    return content;
  }

  return (
    <Modal
      title={<DraggableTitle title="Message" />}
      modalRender={modal => <DraggableWrapper>{modal}</DraggableWrapper>}
      open={open}
      footer={NULLIFY}
      zIndex="1810"
      onCancel={onClose}
      maskClosable={false}
      width={
        historyOpen && displayedTab === SEND_MESSAGE_TAB ? MODAL_WIDTH_WITH_HISTORY : MODAL_WIDTH
      }
      centered
      destroyOnClose
      className="drs-modal-css"
    >
      {content}
    </Modal>
  );
};

export default React.memo(SendMessageModal);
